import { debounce } from 'lodash';
import { useState, useCallback, useContext, useMemo, useRef } from 'react';
import queryString from 'querystring';
import {
  Button,
  Icon,
  HideText,
  ForEach,
  Stack,
} from '@fashionnovateam/nova-ui-toolkit';
import { useLocation, useHistory } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import Pagination from '../ReusableComponents/PagePagination';
import ExportModal from '../ReusableComponents/Modals/ExportModal/ExportModal';
import { purchaseOrdersExport } from '../../Lib/Utils/Table/table-export-v2';
import { FeatureFlagsContext } from '../../Lib/Contexts/FeatureFlagsContext';
import PrinterConfiguration from '../ReusableComponents/PrinterConfiguration/PrinterConfiguration';
import Page from '../Page';
import Header from '../Header';
import { PoCard } from '../PoCard';
import UserAPI from '../../Lib/Api/user';
import { SelectModal } from '../SelectModal';
import { Actions, hasPermissionTo } from '../../Lib/Constants/roles';
import { DateFilter } from '../DateFilter';
import { EmptyAndLoadingPoPage } from './EmptyAndLoadingPoPage';
import {
  useGetUserPurchaseOrders,
  useAcceptPurchaseOrder,
  PaginatedSearchParams,
  PoFilters,
} from '../../Lib/Hooks/purchase-orders.hooks';
import { DateFilterValue } from '../DateFilter/DateFilterCard';
import PurchaseOrderFilter from './PurchaseOrderFilter';
import { User } from '../../Lib/Api/vendor';
import {
  parsePaginatedSearchParams,
  parsePaginatedSearchParamsToQueryString,
} from './utils';
import { isPrintingApproved } from '../../Lib/Utils/Asn/printingApproved';
import { useCurrentUser } from '../../Lib/Contexts/CurrentUserContext';

interface Props {
  user: User;
}

function PurchaseOrderList({ user }: Props) {
  const location = useLocation();
  const queryClient = useQueryClient();
  const history = useHistory();
  const { flags } = useContext(FeatureFlagsContext);
  const { currentUser } = useCurrentUser();
  const poListContainerRef = useRef<HTMLDivElement>(null);

  const params: PaginatedSearchParams = useMemo(
    () =>
      parsePaginatedSearchParams(
        location.search,
        flags.cancelled_po?.enabled,
        flags.po_revision?.enabled,
      ),
    [location.search, flags.cancelled_po?.enabled, flags.po_revision?.enabled],
  );

  const userId = useMemo(() => location.pathname.split('/')[2], [location]);
  const { isLoading, data, refetch } = useGetUserPurchaseOrders(userId, params);
  const {
    mutate: acceptPurchaseOrderMutation,
    isLoading: isAcceptPurchaseOrderLoading,
  } = useAcceptPurchaseOrder();
  const [exporting, setExporting] = useState(false);
  const [openExport, setOpenExport] = useState(false);
  const [selectedPos, setSelectedPos] = useState<string[]>([]);

  const perms = {
    canExport: hasPermissionTo(
      'users/:id/purchase-orders',
      user.role,
      Actions.READ,
      'EXPORT',
    ),
    canAccept: hasPermissionTo(
      'users/:id/purchase-orders',
      user.role,
      Actions.UPDATE,
      'ACCEPT_PO',
    ),
    canPrint: hasPermissionTo(
      'users/:id/purchase-orders',
      user.role,
      Actions.UPDATE,
      'PRINT',
    ),
    canReject: hasPermissionTo(
      'users/:id/purchase-orders',
      user.role,
      Actions.UPDATE,
      'REJECT',
    ),
    canUpdateBulkStatus: hasPermissionTo(
      'users/:id/purchase-orders',
      user.role,
      Actions.UPDATE,
      'UPDATE_BULK_STATUS',
    ),
    canViewCost: hasPermissionTo(
      'users/:id/purchase-orders',
      user.role,
      Actions.READ,
      'VIEW_COST',
    ),
    canCreateRevision: hasPermissionTo(
      'users/:id/purchase-orders',
      user.role,
      Actions.UPDATE,
      'CREATE_REVISION',
    ),
  };

  const { purchaseOrders } = data ?? {};

  const updateUrl = useCallback(
    (paramObject: Partial<PaginatedSearchParams>) => {
      const newParams: PaginatedSearchParams = { ...params, ...paramObject };

      history.push({
        pathname: location.pathname,
        search: queryString.encode(
          parsePaginatedSearchParamsToQueryString(newParams),
        ),
      });
      setSelectedPos([]);
    },
    [location, history],
  );

  const handleCleanSelectedPos = () => {
    // Move focus to a safe place (the list container) before removing modal
    poListContainerRef.current?.focus();

    setSelectedPos([]);
  };

  const handleChangePage = (page: number) => updateUrl({ page: page - 1 });

  const handleStatusFilter = (filter: PoFilters) => {
    // If the filter is empty, remove is_cancelled and sub_stages filters
    if (Object.keys(filter).length === 0 && filter.constructor === Object) {
      updateUrl({
        page: undefined,
        filter: {
          ...params.filter,
          is_cancelled: undefined,
          sub_stages: undefined,
          vendor_revision_status: undefined,
        },
      });
    } else {
      updateUrl({
        page: undefined,
        filter: {
          ...params.filter,
          vendor_revision_status: filter.vendor_revision_status,
          is_cancelled: filter.is_cancelled,
          sub_stages: filter.sub_stages,
        },
      });
    }
  };

  const handleDateFilter = useCallback(
    (dates?: DateFilterValue) => {
      if (!dates) {
        updateUrl({
          filter: {
            ...params.filter,
            due_date: undefined,
          },
          page: undefined,
        });
        return;
      }

      updateUrl({
        filter: {
          ...params.filter,
          due_date: { from: dates.from, to: dates.to },
        },
        page: undefined,
      });
    },
    [updateUrl],
  );

  const handleCleanFilters = () => {
    updateUrl({
      filter: undefined,
      search: undefined,
      page: undefined,
    });
  };

  const toggleExportModal = (status: boolean) => setOpenExport(status);

  const updateSelectedPos = (poId: string, selected: boolean) => {
    setSelectedPos((prev) =>
      selected ? [...prev, poId] : prev.filter((id) => id !== poId),
    );
  };

  const handleConfirmPrint = () => {
    refetch();
  };

  const getPoCount = useCallback(
    async (isActive: boolean, dates?: DateFilterValue) => {
      const usedFilter = dates
        ? {
            filterDateBy: 'due_date',
            from: dates.from,
            to: dates.to,
          }
        : {};

      const filterWithoutDate = params.filter
        ? params.filter.sub_stages?.join(',')
        : null;

      const search = {
        ...usedFilter,
        ...(isActive && params.search && { search: params.search }),
        ...(params.sort && { sortBy: params.sort }),
        ...(filterWithoutDate && { filter: filterWithoutDate }),
        sortOrder: 'ASC',
        page: 0,
        limit: 0,
      };

      const searchString = queryString.encode(search);

      const res = await queryClient.fetchQuery({
        queryKey: ['getPoCount', userId, searchString],
        staleTime: 1000 * 60,
        queryFn: async () =>
          UserAPI.getPurchaseOrdersCount(userId, searchString),
      });
      return `${res.count.toLocaleString('en-US')} / ${res.quantity.toLocaleString('en-US')}`;
    },
    [userId, params.search, params.sort, params.filter],
  );

  return (
    <Page.Container>
      <ExportModal
        open={openExport}
        onExportClick={async (exportParams: object) => {
          setExporting(true);
          await purchaseOrdersExport(userId, {
            ...exportParams,
            canViewCost: perms.canViewCost,
          });
          setExporting(false);
        }}
        onClose={() => toggleExportModal(false)}
        title="Export purchase orders details"
      />
      <Header.Major icon="file/file-text" title="Purchase Orders">
        {isPrintingApproved(user, currentUser) && <PrinterConfiguration />}
      </Header.Major>
      <Header.Minor
        search={{
          value: params.search || '',
          onChange: debounce(
            (value) =>
              updateUrl({
                filter: undefined,
                search: value,
                page: undefined,
              }),
            500,
          ),
          maxLength: 30,
        }}
      >
        {exporting || isLoading ? (
          <Icon.LoadingCircle />
        ) : (
          <Button
            color="secondary"
            size="sm"
            onClick={() => toggleExportModal(true)}
          >
            <Icon name="download/download-down" />
            <HideText notAbove="lg">Export</HideText>
          </Button>
        )}
      </Header.Minor>
      <DateFilter
        initialValue={params.filter.due_date}
        onChange={handleDateFilter}
        getCount={getPoCount}
      />
      <Stack direction="row" className="overflow-hidden h-full">
        <PurchaseOrderFilter onFilter={handleStatusFilter} />
        <div
          ref={poListContainerRef}
          className="overflow-auto h-full w-full md:p-5 xl:p-6 bg-neutral-100 focus:outline-none"
          tabIndex={-1}
        >
          {isLoading ||
          isAcceptPurchaseOrderLoading ||
          purchaseOrders?.length === 0 ? (
            <EmptyAndLoadingPoPage
              loading={isLoading || isAcceptPurchaseOrderLoading}
              params={params}
              onCleanFilters={handleCleanFilters}
            />
          ) : (
            <div
              className="grid relative"
              data-testid="po-list-container"
              data-loading={isLoading || isAcceptPurchaseOrderLoading}
            >
              <div className="grid p-3 gap-4 xl:grid-cols-[repeat(auto-fit,_minmax(450px,_1fr))] md:gap-8 grid-cols-1 3xl:grid-cols-3 4xl:grid-cols-4">
                <ForEach items={purchaseOrders}>
                  {(purchaseOrder) => (
                    <PoCard
                      perms={perms}
                      key={purchaseOrder.po_number}
                      purchaseOrder={purchaseOrder}
                      userId={userId}
                      user={user}
                      loading={isLoading}
                      onAcceptPurchaseOrders={(userId_, poId) =>
                        acceptPurchaseOrderMutation({
                          userId: userId_,
                          purchaseOrderId: poId,
                        })
                      }
                      onRejectPurchaseOrders={() =>
                        console.log('rejectPurchaseOrders')
                      }
                      onConfirmPrint={handleConfirmPrint}
                      selected={selectedPos.includes(purchaseOrder.po_number)}
                      onSelect={(selected) =>
                        updateSelectedPos(purchaseOrder.po_number, selected)
                      }
                      onPoSubStageUpdate={() => {}}
                    />
                  )}
                </ForEach>
              </div>
            </div>
          )}
        </div>
      </Stack>
      {selectedPos.length > 0 && purchaseOrders ? (
        <SelectModal
          selectedPos={selectedPos}
          purchaseOrders={purchaseOrders}
          cleanSelectedPos={handleCleanSelectedPos}
        />
      ) : null}
      <Pagination
        count={data?.count || 0}
        rowsPerPage={params.size || 10}
        page={Number(params.page || 0) + 1}
        onChangePage={handleChangePage}
      />
    </Page.Container>
  );
}

export default PurchaseOrderList;
